import { z } from 'zod';
import { BPM_BUCKET_CODES } from '../utils/bpm-buckets.js';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Admin showcase contracts — venue-category-discovery arch §3.3, ADR-036.
 *
 * The «витрина» of a preset is the pair of things a tenant can discover it by:
 * the venue CATEGORIES it suits (an explicit m2m, D-2) and the facet TAGS it
 * carries (day part / mood / energy / genre, also D-2). A curator confirms
 * both; the machine only ever *suggests*, and since D-31 it suggests the day
 * part and the tags — never the categories.
 */
/**
 * How much the stored suggestion is worth (§4.1.5). `low` is the value the
 * bulk apply refuses to act on (D-13: never mark blind).
 */
export const showcaseConfidenceSchema = z.enum(['high', 'medium', 'low']);
/**
 * The three parts of a day (D-11), one-to-one with
 * `customers.schedule_entries.part` and therefore with `schedulePartSchema`
 * (`./playback.js`).
 *
 * DECLARED HERE, NOT IMPORTED, for the same reason `playback.ts` inlines the
 * enum twice: importing `schedulePartSchema` would make this leaf module depend
 * on `playback.js` → `preset.js`, and `venue-format.ts` (which needs both this
 * enum and `presetStatusSchema`) would then close an ESM cycle whose only
 * symptom is a TDZ crash at import time. The agreement is not left to memory —
 * `apps/api/tests/presets/preset-showcase-schemas.test.ts` asserts the two
 * option lists are identical, so a fourth part cannot appear on one side only.
 */
export const dayPartCodeSchema = z.enum(['morning', 'day', 'evening']);
/**
 * The BPM bucket a preset's median tempo fell into (D-31, arch §4.0).
 *
 * The values come from `BPM_BUCKETS` — the numbers 105 / 117 / 124 / 125 have
 * exactly one home, shared with the worker's energy auto-tagging, and this
 * enum must not become a second copy of that vocabulary.
 */
export const bpmBucketCodeSchema = z.enum(BPM_BUCKET_CODES);
/**
 * One tag on the showcase wire. `name` is what the curator sees, `label` is
 * `coalesce(client_name, name)` — resolved SERVER-side (D-12), so the client
 * never computes a fallback. `code` is the machine key: non-null for the
 * `day_part` tags, whose correspondence with the schedule slots must not be
 * bound to a renameable label (the `energy_range` failure ADR-031 documents).
 */
export const showcaseTagRefSchema = z
    .object({
    tag_id: uuidSchema,
    category_code: z.string(),
    name: z.string(),
    label: z.string(),
    code: z.string().nullable(),
})
    .strict();
/**
 * `PUT /admin/presets/:id/showcase` body — the curator's confirmed set,
 * replace-whole-set. Unknown or out-of-scope ids fail loudly with a 422 and a
 * specific code; they are never dropped silently (the fail-closed discipline of
 * ADR-031).
 */
export const presetShowcaseConfirmedSchema = z
    .object({
    venue_format_ids: z.array(uuidSchema).max(20),
    day_part_codes: z.array(dayPartCodeSchema).max(3),
    tag_ids: z.array(uuidSchema).max(30),
})
    .strict();
/**
 * The stored machine suggestion — NUMBERS ONLY, no Russian prose (DV-3 /
 * design DD-12). The admin assembles «Медиана BPM 118 по 214 трекам — корзина
 * „118–124" → Вечер» from these fields plus `BPM_BUCKETS_BY_CODE`, so the copy
 * stays in the UI layer, stays localisable, and the bucket's edges exist once.
 *
 * `venue_formats` is deliberately ABSENT (D-31). The curator still assigns
 * categories — that is the whole `PUT` — but nothing computes them, and a
 * permanently empty array would read as «система посмотрела и не нашла» when
 * the truth is «система про категории не высказывается».
 */
export const presetShowcaseSuggestionSchema = z
    .object({
    computed_at: isoDateTimeSchema,
    source_version_id: uuidSchema.nullable(),
    /** `source_version_id <> presets.current_version_id` — the preset was
     *  re-published after this was computed. */
    stale: z.boolean(),
    confidence: showcaseConfidenceSchema,
    track_count: z.number().int().nonnegative(),
    bpm_track_count: z.number().int().nonnegative(),
    bpm_median: z.number().nullable(),
    bpm_p25: z.number().nullable(),
    bpm_p75: z.number().nullable(),
    /** The bucket `bpm_median` fell into. `null` iff `bpm_median` is null. The
     *  bucket's numeric edges are NOT on the wire — the admin reads the same
     *  shared table the server used. */
    bpm_bucket: bpmBucketCodeSchema.nullable(),
    /** 0 or 1 entry since D-31: one median → one bucket → one part. */
    day_part_codes: z.array(dayPartCodeSchema).max(1),
    tags: z.array(showcaseTagRefSchema),
    warnings: z.array(z.string()),
})
    .strict();
/** `GET /admin/presets/:id/showcase` — confirmed links plus the stored suggestion. */
export const presetShowcaseResponseSchema = z
    .object({
    preset_id: uuidSchema,
    confirmed: z
        .object({
        venue_formats: z.array(z
            .object({ id: uuidSchema, code: z.string(), name: z.string() })
            .strict()),
        tags: z.array(showcaseTagRefSchema),
        /** How the confirmed set was last written. `null` when nothing is
         *  confirmed yet — distinct from `'manual'` with an empty set, which
         *  means a curator deliberately cleared it. */
        source: z.enum(['manual', 'suggestion', 'mixed']).nullable(),
    })
        .strict(),
    suggestion: presetShowcaseSuggestionSchema.nullable(),
})
    .strict();
/**
 * How many venue categories a list row carries inline before the rest becomes a
 * «+N» (design §14.2 — «до 2 категорий + +N»). Exported so the column and the
 * server agree on the same number.
 */
export const PRESET_LIST_SHOWCASE_FORMAT_CAP = 2;
/** A category on a list row — just enough to render a chip. */
export const presetListShowcaseFormatSchema = z
    .object({ id: uuidSchema, name: z.string() })
    .strict();
/**
 * Compact showcase block on a `GET /admin/presets` row — the data behind the
 * «Витрина» column (design §14.2, requirement §19.11а).
 *
 * FOUR ROW STATES, TWO INDEPENDENT FACTS. The column distinguishes «размечено»,
 * «не размечено», «категории есть, частей дня нет» and «части дня есть,
 * категорий нет», so «есть категории» and «есть части дня» must arrive
 * SEPARATELY. After a bulk run the fourth state is the most common one in the
 * table — the job filled parts and tags and left the categories to the curator
 * (D-31) — and folding the two facts into one flag would make that work
 * invisible.
 *
 * `venue_formats` is capped and `venue_formats_total` carries the real count, so
 * a preset in eleven categories costs the same on the wire as one in two.
 */
export const presetListShowcaseSchema = z
    .object({
    venue_formats: z
        .array(presetListShowcaseFormatSchema)
        .max(PRESET_LIST_SHOWCASE_FORMAT_CAP),
    /** Total confirmed categories; `venue_formats` shows the first few. */
    venue_formats_total: z.number().int().nonnegative(),
    day_part_codes: z.array(dayPartCodeSchema).max(3),
    /** `null` when nothing has been computed for this preset yet. */
    suggestion_confidence: showcaseConfidenceSchema.nullable(),
})
    .strict();
/**
 * Body of both `POST /admin/presets/showcase/bulk-apply/preview` and
 * `POST /admin/presets/showcase/bulk-apply` (D-13). The 500 cap is the runner's
 * per-job selection limit, not a page size.
 */
export const presetShowcaseBulkRequestSchema = z
    .object({
    preset_ids: z.array(uuidSchema).min(1).max(500),
})
    .strict();
/**
 * Pre-flight counts the confirm dialog shows BEFORE the job launches
 * (design §14.3, review F-5).
 *
 * THREE numbers, not two. `needs_recompute` is the honest third one: those
 * presets are computed inside the job and only then follow the high/medium/low
 * rule, so their outcome is genuinely unknown beforehand. On day one it is the
 * whole selection; after a few weeks of publishes it trends to zero. Folding it
 * into `will_apply` would promise an outcome the server cannot know.
 */
export const presetShowcaseBulkPreviewResponseSchema = z
    .object({
    total: z.number().int().nonnegative(),
    /** Stored suggestion present, not stale, confidence `high` or `medium`. */
    will_apply: z.number().int().nonnegative(),
    will_skip_low_confidence: z.number().int().nonnegative(),
    /** No suggestion at all, or stale against `presets.current_version_id`. */
    needs_recompute: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=preset-showcase.js.map